import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EntityService } from '../../../services/entity.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NotificationService } from '../../../services/notification.service';
import { Role, Permission } from '../../../interfaces/user.interface';
import { EditRoleModalComponent } from './edit-role-modal/edit-role-modal.component';
import { ConfirmationModalComponent } from '../../../shared/confirmation-modal/confirmation-modal.component';

@Component({
  selector: 'app-admin-roles',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="roles-container">
      <div class="header">
        <h2>Gestion des Rôles</h2>
        <button class="btn btn-primary" (click)="addRole()">
          Ajouter un rôle
        </button>
      </div>

      <div class="loader-container text-center" *ngIf="loading">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Chargement...</span>
        </div>
      </div>

      <table class="roles-table" *ngIf="!loading">
        <thead>
          <tr>
            <th>Nom</th>
            <th>Nom affiché</th>
            <th>Permissions</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let role of roles">
            <td>{{role.name}}</td>
            <td>{{role.display_name}}</td>
            <td>
            <div class="permission-container">
              <div class="permissions-list">
                <span class="permission-tag" *ngFor="let perm of role.permissions">
                  {{perm.description}}
                  <button class="close-button" title="Retirer cette permission" (click)="removePermission(role, perm)">x</button>
                </span>
              </div>
            </div>
              
            </td>
            <td class="actions">
              <button class="btn btn-xs btn-primary" (click)="editRole(role)">
                <i class="fas fa-edit"></i> 
              </button>
              <button class="btn btn-xs btn-danger" (click)="deleteRole(role)">
                <i class="fas fa-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  `,
  styleUrls: ['./admin-roles.component.scss']
})
export class AdminRolesComponent implements OnInit {
  roles: Role[] = [];
  loading = false;

  constructor(
    private entityService: EntityService,
    private modalService: NgbModal,
    private notificationService: NotificationService
  ) {}

  ngOnInit() {
    this.loadRoles();
  }

  loadRoles() {
    this.loading = true;
    this.entityService.getDatas('/roles').subscribe({
      next: (response: any) => {
        this.roles = response;
        this.loading = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement des rôles:', error);
        this.loading = false;
        this.notificationService.error('Erreur lors du chargement des rôles');
      }
    });
  }

  addRole() {
    const modalRef = this.modalService.open(EditRoleModalComponent, {
      size: 'lg',
      centered: true
    });

    modalRef.result.then((result) => {
      if (result) {
        this.entityService.saveData('/admin/roles', result).subscribe({
          next: () => {
            this.notificationService.success('Rôle ajouté avec succès');
            this.loadRoles();
          },
          error: (error) => {
            console.error('Erreur lors de l\'ajout:', error);
            this.notificationService.error('Erreur lors de l\'ajout du rôle');
          }
        });
      }
    }).catch(() => {});
  }

  editRole(role: Role) {
    const modalRef = this.modalService.open(EditRoleModalComponent, {
      size: 'lg',
      centered: true
    });
    modalRef.componentInstance.role = {...role};

    modalRef.result.then((result) => {
      if (result) {
        this.entityService.updateDataWithPut('/admin/roles', role.id, result).subscribe({
          next: () => {
            console.log(result);
            this.notificationService.success('Rôle mis à jour avec succès');
            this.loadRoles();
          },
          error: (error) => {
            console.error('Erreur lors de la mise à jour:', error);
            this.notificationService.error('Erreur lors de la mise à jour du rôle');
          }
        });
      }
    }).catch(() => {});
  }

  deleteRole(role: Role) {
    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      size: 'sm',
      centered: true
    });
    modalRef.componentInstance.message = 'Êtes-vous sûr de vouloir supprimer ce rôle ?';
    modalRef.componentInstance.title = 'Confirmation de suppression';

    modalRef.result.then((result) => {
      if (result) {
        this.entityService.deleteData('/admin/roles', role.id).subscribe({
          next: () => {
            this.notificationService.success('Rôle supprimé avec succès');
            this.loadRoles();
          },
          error: (error) => {
            console.error('Erreur lors de la suppression:', error);
            this.notificationService.error('Erreur lors de la suppression du rôle');
          }
        });
      }
    }).catch(() => {});
  }

  removePermission(role: Role, permission: Permission) {
    const index = role.permissions.indexOf(permission);
    if (index !== -1) {
      role.permissions.splice(index, 1);
      // Use perm.id directly as a string
      const updatedPermissions = role.permissions.map(perm => String(perm.id));
      this.entityService.updateDataWithPut('/admin/roles', role.id, { ...role, permissions: updatedPermissions }).subscribe({
        next: () => {
          this.notificationService.success('Permission supprimée avec succès');
        },
        error: (error) => {
          console.error('Erreur lors de la suppression de la permission:', error);
          this.notificationService.error('Erreur lors de la suppression de la permission');
        }
      });
    }
  }
}